import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import SearchableSelect from '../SearchableSelect'
import JobPartyAddress from './JobPartyAddress'
import type { JobPartyRole } from '../../lib/jobPartyAddress'
import CreateCustomerDialog from '../CreateCustomerDialog'
import { fetchAllRows } from '../../lib/fetchAllRows'
import PartyTaxIds from './PartyTaxIds'
import { usePartyTaxIds } from '../../hooks/usePartyTaxIds'
import { partyTaxStatus } from '../../lib/partyTaxStatus'
import { canEditCustomers } from '../../lib/customerPermissions'

interface AddressesTabProps {
  jobData: any
  setJobData: (data: any) => void
  readOnly?: boolean
}

interface Customer {
  id: string
  code: string
  name: string
  address_line_1?: string
  address_line_2?: string
  address_line_3?: string
  city?: string
  postcode?: string
  country?: string
  contact_email?: string
  contact_phone?: string
  is_active?: boolean
}

interface Shed {
  id: string
  code: string
  name: string | null
}

// Maps a job's party field to the party_role stored on job_address_overrides.
const FIELD_TO_PARTY_ROLE: Record<string, string> = {
  importer_id: 'importer',
  consignor_id: 'consignor',
  notify_id: 'notify',
  hbl_importer_id: 'hbl_importer',
  hbl_consignor_id: 'hbl_consignor',
  hbl_notify_id: 'hbl_notify',
}

// Z20: BoxTop-style "CODE - NAME" option, with an inline warning when customer.code is
// missing -- code is also what becomes the BoxTop Account_Number (Z18), so a customer
// without one can't be synced as a real BoxTop contact yet.
function customerOption(c: Customer) {
  // A deactivated company that is still stored on the job is shown, clearly marked -- never as a blank box (a blank box looks like
  // "nothing here" and invites staff to pick a new company over the old one without noticing).
  const inactive = c.is_active === false ? '   [INACTIVE company]' : ''
  return { id: c.id, label: `${c.code || '⚠ NO CODE'} - ${c.name}${inactive}`, code: c.code, name: c.name }
}

// Shown under a party box whose stored company is deactivated.
function InactiveNote({ customerId, known }: { customerId: string; known: Customer[] }) {
  const c = customerId ? known.find(x => x.id === customerId) : undefined
  if (!c || c.is_active !== false) return null
  return (
    <p className="mt-1 text-xs font-medium text-red-700 dark:text-red-400">
      This company is INACTIVE (deactivated) but it is still the one stored on this job. Pick another company only if you mean to replace it.
    </p>
  )
}


// L2/L3: SearchableSelect's own input blanks itself the instant it's clicked (onFocus
// opens the dropdown-search), so the selected company name can never be text-selected/
// copied there -- a copy icon sidesteps that instead of touching SearchableSelect's
// focus/blank behavior, which is shared by unrelated dropdowns elsewhere in the app.
// editTarget: only set when the company's EORI is missing or wrong -- then the arrow opens the company with its Edit form on the EORI field.
// Without it the arrow behaves as it always did (opens the company page).
function CompanyFieldActions({ customerId, customers, editTarget }: { customerId: string; customers: Customer[]; editTarget?: 'eori' | null }) {
  const [copied, setCopied] = useState(false)
  const customer = customers.find(c => c.id === customerId)
  if (!customerId || !customer) return null
  const label = `${customer.code} - ${customer.name}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(label)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      console.error('Error copying company name:', error)
    }
  }

  return (
    <span className="inline-flex items-center gap-1 ml-2 align-middle">
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy company name'}
        aria-label="Copy company name"
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        {copied ? (
          <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
            <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
          </svg>
        )}
      </button>
      <Link
        to={`/customers/${customerId}${editTarget ? `?edit=${editTarget}` : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        title={editTarget === 'eori' ? 'Open the company and add its EORI' : 'Open customer record'}
        aria-label={editTarget === 'eori' ? 'Open the company and add its EORI' : 'Open customer record'}
        className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
          <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
          <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
        </svg>
      </Link>
    </span>
  )
}

export default function AddressesTab({ jobData, setJobData, readOnly = false }: AddressesTabProps) {
  const { userEmail, userRoles } = useAuth()
  const [customers, setCustomers] = useState<Customer[]>([])
  // Companies stored on THIS job that are not in the active list (deactivated). Used only to show / resolve the stored value;
  // they are never offered as candidates for another field (the candidate list stays active-only).
  const [storedInactive, setStoredInactive] = useState<Customer[]>([])
  // Z20: Overseas Agent / Port Agent options are filtered to customers tagged with the
  // 'Agent' address_type role (same role both fields backfilled from -- see
  // 20260619_address_types.sql) instead of showing every customer.
  const [agentCustomerIds, setAgentCustomerIds] = useState<Set<string>>(new Set())
  const [agentTypeId, setAgentTypeId] = useState<string | null>(null)
  const [sheds, setSheds] = useState<Shed[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogInitialName, setDialogInitialName] = useState('')
  const [dialogFieldType, setDialogFieldType] = useState<'importer' | 'consignor' | 'notify' | 'agent' | 'other'>('other')
  const [pendingField, setPendingField] = useState<string | null>(null)
  // B: only genuinely MBL+HBL jobs (both B/L numbers populated) get the HBL/MBL party split --
  // simple single-doc jobs keep today's flat view. HBL defaults first, matching BoxTop's own
  // Imports (HBL) vs AWB/BL (MBL) tab split.
  // Z58.12: was sea_details-only, so AIR multi-house sub-jobs (hawb_no/mawb_no, no sea_details
  // row at all) never got the split and fell back to the flat importer_id/consignor_id/notify_id
  // fields -- which create-autojobs deliberately sets to the PARENT's shared MBL parties on every
  // child job, not this child's own parsed parties (those are on hbl_importer_id/hbl_consignor_id/
  // hbl_notify_id). Matches the sea-or-air fallback already used in TransportTab.tsx.
  const hasHblMblSplit = !!(
    (jobData.sea_details?.house_bl_no && jobData.sea_details?.master_bl_no) ||
    (jobData.air_details?.hawb_no && jobData.air_details?.mawb_no)
  )
  const [activeParty, setActiveParty] = useState<'hbl' | 'mbl'>('hbl')
  const partyFields = hasHblMblSplit && activeParty === 'hbl'
    ? { importer: 'hbl_importer_id', consignor: 'hbl_consignor_id', notify: 'hbl_notify_id' }
    : { importer: 'importer_id', consignor: 'consignor_id', notify: 'notify_id' }

  // EORI of the company in the Importer box. ONE place for both sets: the HBL and MBL tabs render this same box (partyFields switches
  // the field), so the HBL importer and the MBL importer are each covered when their tab is open.
  const importerTax = usePartyTaxIds(jobData[partyFields.importer] || '')
  const canEdit = canEditCustomers(userRoles)
  const importerEditTarget = partyTaxStatus(importerTax, canEdit)?.editTarget ?? null

  useEffect(() => {
    loadCustomers()
    loadAgentCustomerIds()
    loadSheds()
  }, [])


  const loadCustomers = async () => {
    try {
      setLoading(true)
      // Z88: paged -- unpaged reads are silently capped at 1000 rows.
      const data = await fetchAllRows<any>((from, to) =>
        supabase
          .from('customers')
          .select('id, code, name, address, address_line_1, address_line_2, address_line_3, city, postcode, country, contact_email, contact_phone, is_active')
          .eq('is_active', true)
          .order('name').order('id')
          .range(from, to))
      setCustomers(data)
    } catch (error) {
      console.error('Error loading customers:', error)
    } finally {
      setLoading(false)
    }
  }

  // Every company id stored on this job (HBL and plain sets, agents). Any that is not in the active list is fetched by id, WITHOUT the
  // is_active filter, so the box can show it.
  const storedIds = [
    jobData.importer_id, jobData.consignor_id, jobData.notify_id,
    jobData.hbl_importer_id, jobData.hbl_consignor_id, jobData.hbl_notify_id,
    jobData.overseas_agent_id, jobData.port_agent_id,
  ].filter((x): x is string => !!x)
  const storedKey = storedIds.join(',')
  useEffect(() => {
    if (loading) return
    const have = new Set(customers.map(c => c.id))
    const missing = [...new Set(storedIds)].filter(id => !have.has(id))
    if (missing.length === 0) { setStoredInactive([]); return }
    let cancelled = false
    supabase.from('customers')
      .select('id, code, name, address, address_line_1, address_line_2, address_line_3, city, postcode, country, contact_email, contact_phone, is_active')
      .in('id', missing)
      .then(({ data, error }) => { if (!cancelled && !error) setStoredInactive((data as Customer[]) || []) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, customers, storedKey])
  // Everything the boxes can resolve a stored id against.
  const known = [...customers, ...storedInactive]
  // Candidate options of one box: the active list, plus the stored company of THIS box if it is deactivated.
  const optionsWithCurrent = (currentId: string) => {
    const extra = currentId ? storedInactive.find(c => c.id === currentId) : undefined
    return extra ? [customerOption(extra), ...customers.map(customerOption)] : customers.map(customerOption)
  }

  const loadAgentCustomerIds = async () => {
    const { data: agentType, error: typeErr } = await supabase
      .from('address_types')
      .select('id')
      .eq('code', 'Agent')
      .maybeSingle()
    if (typeErr || !agentType) {
      console.error('Error loading Agent address_type:', typeErr)
      return
    }
    setAgentTypeId(agentType.id)
    try {
      const data = await fetchAllRows<{ customer_id: string }>((from, to) =>
        supabase
          .from('customer_address_types')
          .select('customer_id')
          .eq('address_type_id', agentType.id)
          .order('customer_id')
          .range(from, to))
      setAgentCustomerIds(new Set(data.map(r => r.customer_id)))
    } catch (error) {
      console.error('Error loading agent customer_address_types:', error)
    }
  }

  const loadSheds = async () => {
    const { data, error } = await supabase.from('sheds').select('id, code, name').eq('is_active', true).order('code')
    if (error) {
      console.error('Error loading sheds:', error)
      return
    }
    setSheds(data || [])
  }


  const updateField = (field: string, value: string) => {
    setJobData({ ...jobData, [field]: value })
  }

  // Overseas / Port Agent: the list used to be ONLY the companies tagged 'Agent' (Z20), so a real company that was not tagged
  // (LANDR, already the agent on 6 jobs) showed "No matches found" plus "+ Create" -- inviting a duplicate. Now every company can be
  // searched; the tagged agents come first, the others say so, and choosing one tags it as an Agent (the explicit pick is the tag).
  const agentOptions = useMemo(() => {
    const opt = (c: Customer) => agentCustomerIds.has(c.id)
      ? customerOption(c)
      : { ...customerOption(c), label: `${c.code || '⚠ NO CODE'} - ${c.name}   (not tagged Agent yet — choosing it tags it)` }
    return [...customers].sort((a, b) => Number(agentCustomerIds.has(b.id)) - Number(agentCustomerIds.has(a.id))).map(opt)
  }, [customers, agentCustomerIds])

  const chooseAgent = async (field: 'overseas_agent_id' | 'port_agent_id', id: string) => {
    updateField(field, id)
    if (id && agentTypeId && !agentCustomerIds.has(id)) {
      const { error: tagErr } = await supabase.from('customer_address_types').insert({ customer_id: id, address_type_id: agentTypeId })
      if (tagErr) console.error('Error tagging customer as Agent:', tagErr)
      else setAgentCustomerIds(prev => new Set([...prev, id]))
    }
  }

  const handleCreateCustomer = (name: string, fieldName: string): Promise<void> => {
    console.log('[AddressesTab] Opening customer creation dialog for field:', fieldName)
    setDialogInitialName(name)
    setPendingField(fieldName)

    // Determine field type for default customer_type
    let fieldType: 'importer' | 'consignor' | 'notify' | 'agent' | 'other' = 'other'
    if (fieldName === 'importer_id') fieldType = 'importer'
    else if (fieldName === 'consignor_id') fieldType = 'consignor'
    else if (fieldName === 'notify_id') fieldType = 'notify'
    else if (fieldName === 'overseas_agent_id' || fieldName === 'port_agent_id') fieldType = 'agent'

    setDialogFieldType(fieldType)
    setDialogOpen(true)
    // Return promise that resolves when dialog completes
    return Promise.resolve()
  }

  const handleDialogSuccess = async (customer: { id: string; code: string; name: string }, address?: { address_line_1: string; address_line_2: string; city: string; postcode: string; country: string }) => {
    console.log('[AddressesTab] Customer created successfully:', customer)
    // Add new customer to list
    const newCustomer: Customer = {
      id: customer.id,
      code: customer.code,
      name: customer.name,
      contact_email: undefined,
      contact_phone: undefined,
      // The address just typed, so the preview shows it straight away (it is saved in the address book; this only fills the in-memory object).
      ...(address ? { address_line_1: address.address_line_1, address_line_2: address.address_line_2, city: address.city, postcode: address.postcode, country: address.country } : {}),
    }
    setCustomers([...customers, newCustomer])

    // Z20: a customer created via the Overseas/Port Agent field's "+ Create" flow must be
    // tagged 'Agent' immediately, or the hard filter on those two dropdowns would make it
    // vanish from the very field that just created it.
    if (dialogFieldType === 'agent' && agentTypeId) {
      const { error: tagErr } = await supabase
        .from('customer_address_types')
        .insert({ customer_id: customer.id, address_type_id: agentTypeId })
      if (tagErr) {
        console.error('Error tagging new customer as Agent:', tagErr)
      } else {
        setAgentCustomerIds(prev => new Set([...prev, customer.id]))
      }
    }

    // Update the job data with the new customer
    if (pendingField) {
      updateField(pendingField, customer.id)
    }

    // Close dialog
    setDialogOpen(false)
    setPendingField(null)
    setDialogInitialName('')
  }

  if (loading) {
    return <div className="text-center py-8">Loading customers...</div>
  }

  return (
    <div className="space-y-6">
      {/* Customer (jobs.invoice_to_id) is NOT here: it is a job-level party shown in the job header (JobCustomerLine). */}
      {hasHblMblSplit && (
        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
          {(['hbl', 'mbl'] as const).map(party => (
            <button
              key={party}
              type="button"
              onClick={() => setActiveParty(party)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                activeParty === party
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {party === 'hbl' ? 'HBL Party' : 'MBL Party'}
            </button>
          ))}
        </div>
      )}

      {/* Consignor */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {hasHblMblSplit && activeParty === 'hbl' ? 'HBL ' : ''}Consignor / Shipper / Exporter <span className="text-red-500">*</span>
        </label>
        <SearchableSelect
          options={optionsWithCurrent(jobData[partyFields.consignor] || '')}
          codeKey="code" nameKey="name"
          value={jobData[partyFields.consignor] || ''}
          onChange={(value) => updateField(partyFields.consignor, value)}
          disabled={readOnly}
          placeholder="Search consignor/shipper/exporter..."
          onCreateClick={(name) => handleCreateCustomer(name, partyFields.consignor)}
        />
        <CompanyFieldActions customerId={jobData[partyFields.consignor] || ''} customers={known} />
        <InactiveNote customerId={jobData[partyFields.consignor] || ''} known={known} />
        <JobPartyAddress jobId={jobData.id} partyRole={FIELD_TO_PARTY_ROLE[partyFields.consignor] as JobPartyRole} customerId={jobData[partyFields.consignor] || ''} userEmail={userEmail} readOnly={readOnly} refreshKey={String((jobData as Record<string, unknown>).updated_at ?? '')} />
      </div>

      {/* Importer */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {hasHblMblSplit && activeParty === 'hbl' ? 'HBL ' : ''}Importer / Consignee <span className="text-red-500">*</span>
        </label>
        <SearchableSelect
          options={optionsWithCurrent(jobData[partyFields.importer] || '')}
          codeKey="code" nameKey="name"
          value={jobData[partyFields.importer] || ''}
          onChange={(value) => updateField(partyFields.importer, value)}
          disabled={readOnly}
          placeholder="Search importer/consignee..."
          onCreateClick={(name) => handleCreateCustomer(name, partyFields.importer)}
        />
        <CompanyFieldActions customerId={jobData[partyFields.importer] || ''} customers={known} editTarget={importerEditTarget} />
        <InactiveNote customerId={jobData[partyFields.importer] || ''} known={known} />
        {jobData[partyFields.importer] && (
          <PartyTaxIds tax={importerTax} companyName={importerTax.name} canEdit={canEdit} />
        )}
        <JobPartyAddress jobId={jobData.id} partyRole={FIELD_TO_PARTY_ROLE[partyFields.importer] as JobPartyRole} customerId={jobData[partyFields.importer] || ''} userEmail={userEmail} readOnly={readOnly} refreshKey={String((jobData as Record<string, unknown>).updated_at ?? '')} />
      </div>

      {/* Notify Party */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {hasHblMblSplit && activeParty === 'hbl' ? 'HBL ' : ''}Notify Party
        </label>
        <SearchableSelect
          options={optionsWithCurrent(jobData[partyFields.notify] || '')}
          codeKey="code" nameKey="name"
          value={jobData[partyFields.notify] || ''}
          onChange={(value) => updateField(partyFields.notify, value)}
          disabled={readOnly}
          placeholder="Search notify party..."
          onCreateClick={(name) => handleCreateCustomer(name, partyFields.notify)}
        />
        <CompanyFieldActions customerId={jobData[partyFields.notify] || ''} customers={known} />
        <InactiveNote customerId={jobData[partyFields.notify] || ''} known={known} />
        <JobPartyAddress jobId={jobData.id} partyRole={FIELD_TO_PARTY_ROLE[partyFields.notify] as JobPartyRole} customerId={jobData[partyFields.notify] || ''} userEmail={userEmail} readOnly={readOnly} refreshKey={String((jobData as Record<string, unknown>).updated_at ?? '')} />
      </div>

      {/* Overseas Agent */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Overseas Agent
        </label>
        <SearchableSelect
          options={agentOptions}
          codeKey="code" nameKey="name"
          selectedOption={(() => {
            const c = known.find(c => c.id === jobData.overseas_agent_id)
            return c ? customerOption(c) : null
          })()}
          value={jobData.overseas_agent_id || ''}
          onChange={(value) => chooseAgent('overseas_agent_id', value)}
          disabled={readOnly}
          placeholder="Search overseas agent..."
          onCreateClick={(name) => handleCreateCustomer(name, 'overseas_agent_id')}
        />
        <InactiveNote customerId={jobData.overseas_agent_id || ''} known={known} />
        <JobPartyAddress jobId={jobData.id} partyRole={'overseas_agent' as JobPartyRole} customerId={jobData.overseas_agent_id || ''} userEmail={userEmail} readOnly={readOnly} refreshKey={String((jobData as Record<string, unknown>).updated_at ?? '')} />
      </div>

      {/* Port Agent */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Port Agent
        </label>
        <SearchableSelect
          options={agentOptions}
          codeKey="code" nameKey="name"
          selectedOption={(() => {
            const c = known.find(c => c.id === jobData.port_agent_id)
            return c ? customerOption(c) : null
          })()}
          value={jobData.port_agent_id || ''}
          onChange={(value) => chooseAgent('port_agent_id', value)}
          disabled={readOnly}
          placeholder="Search port agent..."
          onCreateClick={(name) => handleCreateCustomer(name, 'port_agent_id')}
        />
        <InactiveNote customerId={jobData.port_agent_id || ''} known={known} />
        <JobPartyAddress jobId={jobData.id} partyRole={'port_agent' as JobPartyRole} customerId={jobData.port_agent_id || ''} userEmail={userEmail} readOnly={readOnly} refreshKey={String((jobData as Record<string, unknown>).updated_at ?? '')} />
      </div>

      {/* Shed / Storage Location */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Shed / Warehouse
        </label>
        <SearchableSelect
          options={sheds.map(s => ({ id: s.id, label: `${s.code} - ${s.name || s.code}`, code: s.code, name: s.name || s.code }))}
          codeKey="code" nameKey="name"
          value={jobData.shed_id || ''}
          onChange={(value) => updateField('shed_id', value)}
          disabled={readOnly}
          placeholder="Search shed..."
          allowCreate={false}
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-2">
          Storage Location
        </label>
        <input
          type="text"
          value={jobData.storage_location || ''}
          onChange={(e) => updateField('storage_location', e.target.value)}
          disabled={readOnly}
          placeholder="e.g. Bay 12, Rack 4"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm dark:bg-gray-700 dark:text-white disabled:bg-gray-100 disabled:cursor-not-allowed"
        />
      </div>

      {/* Create Customer Dialog */}
      <CreateCustomerDialog
        isOpen={dialogOpen}
        initialName={dialogInitialName}
        fieldType={dialogFieldType}
        onClose={() => {
          setDialogOpen(false)
          setPendingField(null)
          setDialogInitialName('')
          setDialogFieldType('other')
        }}
        onSuccess={handleDialogSuccess}
      />
    </div>
  )
}
